import { useState, useEffect, useCallback, useRef, useMemo } from "react";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useAuth } from "../context/AuthContext";

function formatDate(dateStr) {
  if (!dateStr) return "";
  return new Date(dateStr).toLocaleDateString("en-US", {
    month: "short", day: "numeric", year: "numeric",
  });
}

const DAY_MS = 86400000;

const STOP_WORDS = new Set([
  "the","a","an","and","or","but","in","on","at","to","for","of","with",
  "is","was","are","were","be","been","has","have","had","do","did","not",
  "it","its","this","that","they","their","very","so","my","we","i","he",
  "she","you","our","all","from","by","as","up","out","about","over","than",
  "me","us","him","her","who","what","when","where","how","just","too","also",
  "would","could","should","will","really","even","much","more","most","well",
  "got","get","no","yes","if","am","been","every","any","some","there","here",
]);

function applyFilters(reviews, { keyword, sentiment, starFilter, dateFilter, sortBy, unreplied }) {
  let result = reviews.filter((r) => r.comment && r.comment.trim().length > 0);

  if (sentiment === "positive") result = result.filter((r) => r.rating > 3);
  if (sentiment === "negative") result = result.filter((r) => r.rating <= 3);
  if (starFilter !== null)      result = result.filter((r) => r.rating === starFilter);
  if (unreplied)                result = result.filter((r) => !r.has_reply);

  const kw = keyword.trim().toLowerCase();
  if (kw) result = result.filter((r) => r.comment.toLowerCase().includes(kw));

  const now = Date.now();
  const todayStr = new Date().toISOString().split("T")[0];
  if (dateFilter === "today")   result = result.filter((r) => r.date === todayStr);
  if (dateFilter === "week")    result = result.filter((r) => new Date(r.date).getTime() >= now - 7  * DAY_MS);
  if (dateFilter === "month")   result = result.filter((r) => new Date(r.date).getTime() >= now - 30 * DAY_MS);
  if (dateFilter === "3months") result = result.filter((r) => new Date(r.date).getTime() >= now - 90 * DAY_MS);

  if (sortBy === "newest")  result.sort((a, b) => new Date(b.date) - new Date(a.date));
  if (sortBy === "oldest")  result.sort((a, b) => new Date(a.date) - new Date(b.date));
  if (sortBy === "highest") result.sort((a, b) => b.rating - a.rating);
  if (sortBy === "lowest")  result.sort((a, b) => a.rating - b.rating);

  return result;
}

function Stars({ rating, size = "md" }) {
  return (
    <div className={`rv-stars rv-stars-${size}`}>
      {"★".repeat(rating)}{"☆".repeat(5 - rating)}
    </div>
  );
}

function Highlighted({ text, keyword }) {
  if (!keyword.trim()) return <>{text}</>;
  const parts = text.split(new RegExp(`(${keyword.trim()})`, "gi"));
  return (
    <>
      {parts.map((part, i) =>
        part.toLowerCase() === keyword.trim().toLowerCase()
          ? <mark key={i} className="rv-highlight">{part}</mark>
          : part
      )}
    </>
  );
}

function exportCSV(reviews, businessName) {
  const header = ["Author", "Rating", "Sentiment", "Date", "Comment"];
  const rows   = reviews.map((r) => [
    `"${(r.author || "Anonymous").replace(/"/g, '""')}"`,
    r.rating,
    r.rating > 3 ? "Positive" : "Negative",
    r.date || "",
    `"${(r.comment || "").replace(/"/g, '""')}"`,
  ]);
  const csv  = [header, ...rows].map((r) => r.join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv" });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement("a");
  a.href     = url;
  a.download = `${businessName}_reviews.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

function exportPDF(reviews, businessName) {
  const doc = new jsPDF();
  doc.setFontSize(16);
  doc.setTextColor(30, 27, 75);
  doc.text(`${businessName} — Review Report`, 14, 18);
  doc.setFontSize(9);
  doc.setTextColor(120, 120, 160);
  doc.text(`Generated by RIRA · ${new Date().toLocaleDateString()}`, 14, 25);

  autoTable(doc, {
    startY: 30,
    head: [["Author", "Rating", "Sentiment", "Date", "Comment"]],
    body: reviews.map((r) => [
      r.author || "Anonymous",
      r.rating,
      r.rating > 3 ? "Positive" : "Negative",
      r.date || "",
      r.comment,
    ]),
    headStyles:  { fillColor: [26, 26, 26], textColor: 255, fontStyle: "bold" },
    alternateRowStyles: { fillColor: [245, 245, 245] },
    columnStyles: { 4: { cellWidth: 100 } },
    styles: { fontSize: 8, cellPadding: 3 },
  });

  doc.save(`${businessName}_reviews.pdf`);
}

function SummaryCard({ reviews, businessName }) {
  const { token }               = useAuth();
  const [summary, setSummary]   = useState("");
  const [loading, setLoading]   = useState(false);
  const cache                   = useRef({});

  useEffect(() => {
    if (!businessName || !reviews?.length) return;
    if (cache.current[businessName]) { setSummary(cache.current[businessName]); return; }

    setLoading(true);
    setSummary("");
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/generate-summary`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ business_name: businessName, reviews }),
    })
      .then((r) => r.json())
      .then((d) => {
        cache.current[businessName] = d.summary;
        setSummary(d.summary);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [businessName, reviews]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!loading && !summary) return null;

  return (
    <div className="rv-summary-card">
      <div className="rv-sc-body">
        <span className="rv-sc-label">AI Summary</span>
        {loading ? (
          <div className="rv-sc-loading">
            <div className="rv-sc-spinner" /> Generating summary...
          </div>
        ) : (
          <p className="rv-sc-text">{summary}</p>
        )}
      </div>
    </div>
  );
}

function KeywordPanel({ reviews, onKeywordClick, activeKeyword }) {
  const keywords = useMemo(() => {
    const freq = {};
    (reviews || []).forEach((r) => {
      r.comment
        .toLowerCase()
        .replace(/[^a-z\s]/g, "")
        .split(/\s+/)
        .forEach((w) => {
          if (w.length > 3 && !STOP_WORDS.has(w)) {
            freq[w] = (freq[w] || 0) + 1;
          }
        });
    });
    return Object.entries(freq)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([word, count]) => ({ word, count }));
  }, [reviews]);

  if (keywords.length === 0) return null;

  return (
    <div className="rv-keyword-panel">
      <span className="rv-kp-label">Top Keywords</span>
      <div className="rv-kp-chips">
        {keywords.map(({ word, count }) => {
          const active = activeKeyword.toLowerCase() === word;
          return (
            <button
              key={word}
              className={`rv-kp-chip ${active ? "active" : ""}`}
              onClick={() => onKeywordClick(active ? "" : word)}
            >
              {word}
              <span className="rv-kp-count">{count}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

const SHARE_PLATFORMS = [
  {
    id: "copy",
    label: "Copy",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <rect x="9" y="9" width="13" height="13" rx="2"/><path d="M5 15H4a2 2 0 01-2-2V4a2 2 0 012-2h9a2 2 0 012 2v1"/>
      </svg>
    ),
  },
  {
    id: "whatsapp",
    label: "WhatsApp",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M21 11.5a8.38 8.38 0 01-.9 3.8 8.5 8.5 0 01-7.6 4.7 8.38 8.38 0 01-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 01-.9-3.8 8.5 8.5 0 014.7-7.6 8.38 8.38 0 013.8-.9h.5a8.48 8.48 0 018 8v.5z"/>
      </svg>
    ),
  },
  {
    id: "facebook",
    label: "Facebook",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M18 2h-3a5 5 0 00-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 011-1h3z"/>
      </svg>
    ),
  },
  {
    id: "twitter",
    label: "Twitter / X",
    icon: (
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M23 3a10.9 10.9 0 01-3.14 1.53 4.48 4.48 0 00-7.86 3v1A10.66 10.66 0 013 4s-4 9 5 13a11.64 11.64 0 01-7 2c9 5 20 0 20-11.5a4.5 4.5 0 00-.08-.83A7.72 7.72 0 0023 3z"/>
      </svg>
    ),
  },
  {
    id: "gmail",
    label: "Gmail",
    icon: (
      <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"/><polyline points="22,6 12,13 2,6"/>
      </svg>
    ),
  },
];

function ShareDropdown({ onSelect }) {
  return (
    <div className="rv-share-dropdown">
      {SHARE_PLATFORMS.map((p) => (
        <button key={p.id} className="rv-share-item" onClick={() => onSelect(p.id)}>
          <span className="rv-share-item-icon">{p.icon}</span>
          <span className="rv-share-item-label">{p.label}</span>
        </button>
      ))}
    </div>
  );
}

const TONES_NEGATIVE = [
  { id: "professional", label: "Professional" },
  { id: "friendly",     label: "Friendly" },
  { id: "apologetic",   label: "Apologetic" },
];
const TONES_POSITIVE = [
  { id: "professional", label: "Professional" },
  { id: "friendly",     label: "Friendly" },
  { id: "enthusiastic", label: "Enthusiastic" },
];

function DraftReplyPanel({ review, businessName, onClose, googleConnected = false }) {
  const { token }                     = useAuth();
  const [reply, setReply]             = useState("");
  const [loading, setLoading]         = useState(false);
  const [error, setError]             = useState("");
  const [shareOpen, setShareOpen]     = useState(false);
  const [postLoading, setPostLoading] = useState(false);
  const [postMsg, setPostMsg]         = useState({ text: "", ok: false });
  const [tone, setTone]               = useState("professional");
  const shareWrapRef                  = useRef(null);
  const tones = review.rating > 3 ? TONES_POSITIVE : TONES_NEGATIVE;

  useEffect(() => {
    if (!shareOpen) return;
    const h = (e) => { if (shareWrapRef.current && !shareWrapRef.current.contains(e.target)) setShareOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [shareOpen]);

  const generate = (selectedTone) => {
    setReply(""); setError(""); setLoading(true); setPostMsg({ text: "", ok: false });
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/generate-reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({
        business_name: businessName || "our business",
        rating: review.rating,
        comment: review.comment,
        tone: selectedTone || "professional",
      }),
    })
      .then((res) => { if (!res.ok) throw new Error(`Server error ${res.status}`); return res.json(); })
      .then((data) => { setReply(data.reply); setLoading(false); })
      .catch((e) => { setError(e.message); setLoading(false); });
  };

  useEffect(() => { generate(tone); }, [review, businessName]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleToneChange = (newTone) => {
    setTone(newTone);
    generate(newTone);
  };

  const shareVia = (platform) => {
    const enc = encodeURIComponent(reply);
    const urls = {
      whatsapp: `https://wa.me/?text=${enc}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=https://rirareview.com&quote=${enc}`,
      twitter:  `https://twitter.com/intent/tweet?text=${enc}`,
      gmail:    `mailto:?subject=${encodeURIComponent(`Reply — ${businessName || "Business"}`)}&body=${enc}`,
    };
    if (platform === "copy") {
      navigator.clipboard.writeText(reply);
    } else if (urls[platform]) {
      window.open(urls[platform], "_blank", "noopener");
    }
    setShareOpen(false);
  };

  const handlePostToGoogle = () => {
    if (!reply.trim() || !review.review_name) return;
    setPostLoading(true);
    setPostMsg({ text: "", ok: false });
    fetch(`${process.env.REACT_APP_API_URL || "http://127.0.0.1:8000"}/google/post-reply`, {
      method: "POST",
      headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
      body: JSON.stringify({ review_name: review.review_name, reply: reply.trim() }),
    })
      .then((res) => res.ok ? res.json() : res.json().then((d) => Promise.reject(d.detail)))
      .then(() => { setPostMsg({ text: "Reply posted to Google successfully.", ok: true }); setPostLoading(false); })
      .catch((err) => { setPostMsg({ text: typeof err === "string" ? err : "Failed to post reply.", ok: false }); setPostLoading(false); });
  };

  const canPostToGoogle = googleConnected && !!review.review_name;

  return (
    <div className="rv-draft-panel">
      <div className="rv-draft-header">
        <span className="rv-draft-title">AI Draft Reply</span>
        <button className="rv-draft-close" onClick={onClose}>✕</button>
      </div>

      <div className="rv-tone-bar">
        {tones.map((t) => (
          <button
            key={t.id}
            className={`rv-tone-btn ${tone === t.id ? "rv-tone-btn--active" : ""}`}
            onClick={() => handleToneChange(t.id)}
            disabled={loading}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading && (
        <div className="rv-draft-loading">
          <div className="rv-draft-spinner" />
          Generating response...
        </div>
      )}

      {error && <div className="rv-draft-error">{error}</div>}

      {!loading && !error && reply && (
        <>
          <textarea
            className="rv-draft-textarea"
            value={reply}
            onChange={(e) => setReply(e.target.value)}
          />
          <div className="rv-draft-actions">
            <button className="rv-draft-regen" onClick={() => generate(tone)}>
              Regenerate
            </button>
            <div className="rv-draft-share-wrap" ref={shareWrapRef}>
              <button className="rv-share-btn" onClick={() => setShareOpen((o) => !o)}>
                Share ▾
              </button>
              {shareOpen && <ShareDropdown onSelect={shareVia} />}
            </div>
          </div>
          <div className="rv-draft-google-row">
            {postMsg.text && (
              <div className={`rv-draft-post-msg ${postMsg.ok ? "ok" : "err"}`}>{postMsg.text}</div>
            )}
            <div
              className="rv-post-google-wrap"
              title={!canPostToGoogle ? "Connect Google Business in Settings to enable this" : ""}
            >
              <button
                className={`rv-post-google-btn ${!canPostToGoogle ? "rv-post-google-btn--disabled" : ""}`}
                onClick={canPostToGoogle ? handlePostToGoogle : undefined}
                disabled={!canPostToGoogle || postLoading || !reply.trim() || postMsg.ok}
              >
                {postLoading ? "Posting..." : postMsg.ok ? "Posted" : "Beam it Live"}
              </button>
            </div>
          </div>
        </>
      )}
    </div>
  );
}

function Carousel({ reviews, startIndex, onClose, businessName, googleConnected = false }) {
  const [index, setIndex]         = useState(startIndex);
  const [showDraft, setShowDraft] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);
  const touchStartX               = useRef(null);
  const shareWrapRef              = useRef(null);

  const prev = useCallback(() => { setIndex((i) => (i - 1 + reviews.length) % reviews.length); setShowDraft(false); setShareOpen(false); }, [reviews.length]);
  const next = useCallback(() => { setIndex((i) => (i + 1) % reviews.length); setShowDraft(false); setShareOpen(false); }, [reviews.length]);

  useEffect(() => {
    if (!shareOpen) return;
    const h = (e) => { if (shareWrapRef.current && !shareWrapRef.current.contains(e.target)) setShareOpen(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, [shareOpen]);

  const getShareText = () => {
    const r     = reviews[index];
    const stars = "★".repeat(r.rating) + "☆".repeat(5 - r.rating);
    return `${stars} — ${businessName || "Business Review"}\n"${r.comment}"${r.date ? `\n${formatDate(r.date)}` : ""}\n\nShared via RIRA`;
  };

  const shareVia = (platform) => {
    const text = getShareText();
    const enc  = encodeURIComponent(text);
    const urls = {
      whatsapp: `https://wa.me/?text=${enc}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=https://rirareview.com&quote=${enc}`,
      gmail:    `mailto:?subject=${encodeURIComponent(`Review — ${businessName || "Business"}`)}&body=${enc}`,
      twitter:  `https://twitter.com/intent/tweet?text=${enc}`,
    };
    if (platform === "copy") {
      navigator.clipboard.writeText(text);
    } else if (urls[platform]) {
      window.open(urls[platform], "_blank", "noopener");
    }
    setShareOpen(false);
  };

  useEffect(() => {
    const h = (e) => {
      if (e.key === "ArrowLeft")  prev();
      if (e.key === "ArrowRight") next();
      if (e.key === "Escape")     onClose();
    };
    window.addEventListener("keydown", h);
    return () => window.removeEventListener("keydown", h);
  }, [prev, next, onClose]);

  const onTouchStart = (e) => { touchStartX.current = e.touches[0].clientX; };
  const onTouchEnd   = (e) => {
    if (touchStartX.current === null) return;
    const diff = touchStartX.current - e.changedTouches[0].clientX;
    if (diff > 50)  next();
    if (diff < -50) prev();
    touchStartX.current = null;
  };

  const review = reviews[index];
  const isPos  = review.rating > 3;
  // eslint-disable-next-line no-unused-vars
  const isNeg  = review.rating <= 3;

  return (
    <div className="rv-overlay" onClick={onClose}>
      <div
        className={`rv-modal ${showDraft ? "rv-modal--wide" : ""}`}
        onClick={(e) => e.stopPropagation()}
        onTouchStart={onTouchStart}
        onTouchEnd={onTouchEnd}
      >
        <button className="rv-modal-close" onClick={onClose}>✕</button>
        <div className="rv-modal-counter">{index + 1} / {reviews.length}</div>

        <button className="rv-arrow rv-arrow-left"  onClick={prev}>&#8249;</button>
        <button className="rv-arrow rv-arrow-right" onClick={next}>&#8250;</button>

        <div className={`rv-modal-inner ${showDraft ? "rv-modal-inner--split" : ""}`}>
          <div className="rv-modal-body">
            <div className={`rv-modal-badge ${isPos ? "pos" : "neg"}`}>
              {isPos ? "Positive" : "Needs Attention"}
            </div>
            {review.author && (
              <div className="rv-modal-author-row">
                <div className="rv-modal-avatar">
                  {review.author_image
                    ? <img src={review.author_image} alt={review.author} className="rv-avatar-img" referrerPolicy="no-referrer" />
                    : review.author.charAt(0).toUpperCase()}
                </div>
                <span className="rv-modal-author-name">{review.author}</span>
              </div>
            )}
            <Stars rating={review.rating} size="lg" />
            <p className="rv-modal-comment">{review.comment}</p>
            {review.date && (
              <p className="rv-modal-date">{formatDate(review.date)}</p>
            )}

            <div className="rv-modal-actions">
              <div className="rv-share-wrap" ref={shareWrapRef}>
                <button className="rv-share-btn" onClick={() => setShareOpen((o) => !o)}>
                  Share
                </button>
                {shareOpen && <ShareDropdown onSelect={shareVia} />}
              </div>
              {!showDraft && (
                <button className="rv-draft-btn" onClick={() => setShowDraft(true)}>
                  Reply Direct
                </button>
              )}
            </div>
          </div>

          {showDraft && (
            <DraftReplyPanel
              review={review}
              businessName={businessName}
              onClose={() => setShowDraft(false)}
              googleConnected={googleConnected}
            />
          )}
        </div>

        {!showDraft && (
          <>
            <div className="rv-dots">
              {reviews.map((_, i) => (
                <span key={i} className={`rv-dot ${i === index ? "active" : ""}`} onClick={() => setIndex(i)} />
              ))}
            </div>
            <p className="rv-swipe-hint">Use arrow keys to navigate</p>
          </>
        )}
      </div>
    </div>
  );
}

function ReviewTile({ review, index, onOpen, keyword }) {
  const [copied, setCopied] = useState(false);
  const isPos = review.rating > 3;

  const handleCopy = (e) => {
    e.stopPropagation();
    navigator.clipboard.writeText(review.comment);
    setCopied(true);
    setTimeout(() => setCopied(false), 1500);
  };

  const initial = review.author ? review.author.charAt(0).toUpperCase() : "?";

  return (
    <div className={`rv-tile ${isPos ? "pos" : "neg"}`} onClick={() => onOpen(index)}>
      <div className="rv-tile-author-row">
        <div className="rv-tile-avatar">
          {review.author_image
            ? <img src={review.author_image} alt={review.author} className="rv-avatar-img" referrerPolicy="no-referrer" />
            : initial}
        </div>
        <div className="rv-tile-author-info">
          <span className="rv-tile-author-name">{review.author || "Anonymous"}</span>
          <Stars rating={review.rating} size="sm" />
        </div>
      </div>
      <p className="rv-tile-text">
        <Highlighted text={review.comment} keyword={keyword} />
      </p>
      <div className="rv-tile-footer">
        {review.date && <span className="rv-tile-date">{formatDate(review.date)}</span>}
        <div className="rv-tile-footer-right">
          <span className={`rv-tile-badge ${isPos ? "pos" : "neg"}`}>
            {isPos ? "Positive" : "Attention"}
          </span>
          <button className="rv-tile-copy" onClick={handleCopy} title="Copy review text">
            {copied ? "Copied" : "Copy"}
          </button>
        </div>
      </div>
    </div>
  );
}

function Pagination({ currentPage, totalPages, onPageChange }) {
  if (totalPages <= 1) return null;
  const range = (s, e) => Array.from({ length: e - s + 1 }, (_, i) => s + i);
  let nums;
  if (totalPages <= 7) {
    nums = range(1, totalPages);
  } else {
    const l = Math.max(2, currentPage - 2);
    const r = Math.min(totalPages - 1, currentPage + 2);
    nums = [1];
    if (l > 2) nums.push("…");
    nums.push(...range(l, r));
    if (r < totalPages - 1) nums.push("…");
    nums.push(totalPages);
  }
  return (
    <div className="rv-pagination">
      <button className="rv-page-btn rv-page-btn--nav" onClick={() => onPageChange(currentPage - 1)} disabled={currentPage === 1}>‹</button>
      {nums.map((p, i) =>
        typeof p === "string"
          ? <span key={`e${i}`} className="rv-page-ellipsis">{p}</span>
          : <button key={p} className={`rv-page-btn ${p === currentPage ? "rv-page-btn--active" : ""}`} onClick={() => onPageChange(p)}>{p}</button>
      )}
      <button className="rv-page-btn rv-page-btn--nav" onClick={() => onPageChange(currentPage + 1)} disabled={currentPage === totalPages}>›</button>
    </div>
  );
}

function ReviewList({ reviews, loading, businessName, googleConnected = false }) {
  const [keyword,       setKeyword]       = useState("");
  const [sentiment,     setSentiment]     = useState("all");
  const [starFilter,    setStarFilter]    = useState(null);
  const [dateFilter,    setDateFilter]    = useState("all");
  const [sortBy,        setSortBy]        = useState("newest");
  const [unreplied,     setUnreplied]     = useState(false);
  const [carouselIndex, setCarouselIndex] = useState(null);
  const [linkCopied,    setLinkCopied]    = useState(false);
  const [perPage,       setPerPage]       = useState(20);
  const [currentPage,   setCurrentPage]   = useState(1);

  const clearAll = () => {
    setKeyword(""); setSentiment("all");
    setStarFilter(null); setDateFilter("all"); setSortBy("newest"); setUnreplied(false);
  };

  const activeCount = [
    keyword.trim() !== "",
    sentiment !== "all",
    starFilter !== null,
    dateFilter !== "all",
    unreplied,
  ].filter(Boolean).length;

  const filtered = useMemo(
    () => applyFilters(reviews || [], { keyword, sentiment, starFilter, dateFilter, sortBy, unreplied }),
    [reviews, keyword, sentiment, starFilter, dateFilter, sortBy, unreplied]
  );

  useEffect(() => { setCurrentPage(1); }, [keyword, sentiment, starFilter, dateFilter, sortBy, unreplied, perPage]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / perPage));
  const startIdx   = (currentPage - 1) * perPage;
  const paginated  = filtered.slice(startIdx, startIdx + perPage);
  const gridClass  = perPage <= 10 ? "rv-tile-grid--lg" : perPage >= 50 ? "rv-tile-grid--sm" : "";

  const handleCopyReviewLink = () => {
    const url = `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(businessName)}`;
    navigator.clipboard.writeText(url).then(() => {
      setLinkCopied(true);
      setTimeout(() => setLinkCopied(false), 2500);
    });
  };

  const posCount = (reviews || []).filter((r) => r.rating > 3).length;
  const negCount = (reviews || []).length - posCount;

  if (loading || !reviews || reviews.length === 0) return null;

  return (
    <div className="rv-container">

      <SummaryCard reviews={reviews} businessName={businessName} />

      <KeywordPanel
        reviews={reviews}
        onKeywordClick={setKeyword}
        activeKeyword={keyword}
      />

      <div className="rv-search-row">
        <div className="rv-search-wrap">
          <input
            className="rv-keyword-input"
            type="text"
            placeholder="Search reviews by keyword (e.g. staff, clean, food...)"
            value={keyword}
            onChange={(e) => setKeyword(e.target.value)}
          />
          {keyword && (
            <button className="rv-kw-clear" onClick={() => setKeyword("")}>✕</button>
          )}
        </div>
      </div>

      <div className="rv-filter-bar">
        <div className="rv-select-wrap">
          <select
            className={`rv-select ${sentiment !== "all" ? "rv-select--active" : ""}`}
            value={sentiment}
            onChange={(e) => setSentiment(e.target.value)}
          >
            <option value="all">Sentiment</option>
            <option value="positive">Positive</option>
            <option value="negative">Negative</option>
          </select>
        </div>

        <div className="rv-select-wrap">
          <select
            className={`rv-select ${starFilter !== null ? "rv-select--active" : ""}`}
            value={starFilter ?? ""}
            onChange={(e) => setStarFilter(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">All Ratings</option>
            {[5, 4, 3, 2, 1].map((s) => (
              <option key={s} value={s}>{s} stars</option>
            ))}
          </select>
        </div>

        <div className="rv-select-wrap">
          <select
            className={`rv-select ${dateFilter !== "all" ? "rv-select--active" : ""}`}
            value={dateFilter}
            onChange={(e) => setDateFilter(e.target.value)}
          >
            <option value="all">All Time</option>
            <option value="today">Today</option>
            <option value="week">This Week</option>
            <option value="month">This Month</option>
            <option value="3months">Last 3 Months</option>
          </select>
        </div>

        <div className="rv-select-wrap">
          <select
            className="rv-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="newest">Newest First</option>
            <option value="oldest">Oldest First</option>
            <option value="highest">Highest Rating</option>
            <option value="lowest">Lowest Rating</option>
          </select>
        </div>

        <button
          className={`rv-unreplied-btn ${unreplied ? "rv-unreplied-btn--active" : ""}`}
          onClick={() => setUnreplied((u) => !u)}
          title="Show only reviews without a reply"
        >
          Unreplied only
        </button>

        {activeCount > 0 && (
          <button className="rv-clear-btn" onClick={clearAll}>
            Clear
            <span className="rv-active-badge">{activeCount}</span>
          </button>
        )}
      </div>

      <div className="rv-summary-bar">
        <span className="rv-summary-text">
          {filtered.length > perPage
            ? <>Showing <strong>{startIdx + 1}–{Math.min(startIdx + perPage, filtered.length)}</strong> of <strong>{filtered.length}</strong> reviews</>
            : <><strong>{filtered.length}</strong> review{filtered.length !== 1 ? "s" : ""}</>
          }
          <span className="rv-summary-meta">
            &nbsp;·&nbsp;
            <span className="rv-pos-text">{posCount} positive</span>
            &nbsp;·&nbsp;
            <span className="rv-neg-text">{negCount} negative</span>
          </span>
        </span>
        <div className="rv-export-btns">
          <div className="rv-perpage-group">
            {[10, 20, 50].map((n) => (
              <button
                key={n}
                className={`rv-perpage-btn${perPage === n ? " active" : ""}`}
                onClick={() => { setPerPage(n); setCurrentPage(1); }}
              >{n}</button>
            ))}
          </div>
          <button className="rv-review-link-btn" onClick={handleCopyReviewLink} title="Copy a Google Maps link to share with customers for review requests">
            {linkCopied ? "Link copied!" : "Get Review Link"}
          </button>
          <button className="rv-export-btn" onClick={() => exportCSV(filtered, businessName)}>
            Export CSV
          </button>
          <button className="rv-export-btn rv-export-btn--pdf" onClick={() => exportPDF(filtered, businessName)}>
            Export PDF
          </button>
        </div>
      </div>

      {filtered.length === 0 ? (
        <div className="rv-no-match">
          <p>No reviews match your current filters.</p>
          <button className="rv-clear-btn rv-clear-btn--center" onClick={clearAll}>Clear all filters</button>
        </div>
      ) : (
        <>
          <div className={`rv-tile-grid ${gridClass}`}>
            {paginated.map((r, i) => (
              <ReviewTile key={i} review={r} index={i} onOpen={setCarouselIndex} keyword={keyword} />
            ))}
          </div>
          <Pagination currentPage={currentPage} totalPages={totalPages} onPageChange={setCurrentPage} />
        </>
      )}

      {carouselIndex !== null && (
        <Carousel
          reviews={paginated}
          startIndex={carouselIndex}
          onClose={() => setCarouselIndex(null)}
          businessName={businessName}
          googleConnected={googleConnected}
        />
      )}
    </div>
  );
}

export default ReviewList;
